import jwt_decode from 'jwt-decode'

export type AuthResult = {
  accessToken?: string | null
  refreshToken?: string | null
  ssoCode?: string | null
}

export type HandleAuthResultOptions = {
  requireRefreshToken?: boolean
}

export type HandleAuthResultOutcome =
  | 'authenticated'
  | 'redirected'
  | 'invalid'

type SsoCodePayload = {
  aud?: string
}

export const clearAuthStorage = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('roles')
}

const normalizeRedirectTarget = (audience: string) => {
  const trimmedAudience = audience.trim()

  if (!trimmedAudience) {
    return null
  }

  const baseUrl = /^https?:\/\//.test(trimmedAudience)
    ? trimmedAudience
    : `https://${trimmedAudience}`

  try {
    return new URL(baseUrl)
  } catch (error) {
    return null
  }
}

export const getSsoRedirectUrl = (audience: string, code: string) => {
  const baseUrl = normalizeRedirectTarget(audience)

  if (!baseUrl) {
    return null
  }

  const url = new URL('/sso/callback', baseUrl)
  url.searchParams.set('code', code)

  return url.toString()
}

const getSsoRedirectUrlFromCode = (ssoCode: string) => {
  try {
    const { aud }: SsoCodePayload = jwt_decode(ssoCode)

    if (!aud) {
      return null
    }

    return getSsoRedirectUrl(aud, ssoCode)
  } catch (error) {
    return null
  }
}

export const handleAuthResult = (
  { accessToken, refreshToken, ssoCode }: AuthResult,
  { requireRefreshToken = false }: HandleAuthResultOptions = {},
): HandleAuthResultOutcome => {
  if (ssoCode) {
    const redirectUrl = getSsoRedirectUrlFromCode(ssoCode)

    if (!redirectUrl) {
      return 'invalid'
    }

    clearAuthStorage()
    window.location.replace(redirectUrl)
    return 'redirected'
  }

  if (!accessToken || (requireRefreshToken && !refreshToken)) {
    return 'invalid'
  }

  localStorage.setItem('accessToken', accessToken)

  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  }

  return 'authenticated'
}
